import { AfterViewInit, Component, ElementRef, Input, EventEmitter, Output } from '@angular/core';
import { Feature, MapBrowserEvent } from 'ol';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import { Cluster } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { environment } from 'src/environments/environment';
import * as Proj from 'ol/proj';
import { RegulacionPlazaInformePlanoModel } from 'src/app/core/models/regulacion-plaza-informe-plano.model';
import { TranslateService } from '@ngx-translate/core';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import * as textStyle from 'ol/style/Text';
import * as olExtent from 'ol/extent';
import { OlMapBaseComponent } from '../ol-map-base/ol-map-base.component';
import Layer from 'ol/layer/Layer';

const DEFAULT_ANIMATION_MILLISECONDS = 500;
const CLUSTER_PIXEL_DISTANCE = 38;

const VE_TYPE = 1;
const PMR_TYPE = 2;
const CYD_TYPE = 3;

@Component({
  selector: 'dum-ol-map-plaza-diponibilidad-semanal',
  templateUrl: './ol-map-plaza-diponibilidad-semanal.component.html',
  styleUrls: ['./ol-map-plaza-diponibilidad-semanal.component.scss']
})
export class OlMapPlazaDiponibilidadSemanalComponent extends OlMapBaseComponent implements AfterViewInit {
  @Input() width?: string | number = environment.default_width_map;
  @Input() height?: string | number = environment.default_height_map;
  @Input() plazas?: RegulacionPlazaInformePlanoModel[] = [];
  lat: number = environment.default_lat_map;
  lon: number = environment.default_lon_map;
  srcIconOrigen: string;
  srcIconDestino: string;
  zoom: number = 15;

  itemLayers = [
    {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.VE_TYPE')}`,
      active: true,
      type: VE_TYPE
    }, {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.PMR_TYPE')}`,
      active: true,
      type: PMR_TYPE
    }, {
      label: `${this.translateService.instant('GLOBAL.LAYERSWITCHER.CYD_TYPE')}`,
      active: true,
      type: CYD_TYPE
    }
  ];

  private vectorLayer: VectorLayer;

  constructor(
    protected elementRef: ElementRef,
    protected translateService: TranslateService) {
    super(elementRef, translateService);
  }

  ngAfterViewInit(): void {
    this.initMap('popup-disponibilidad', 'popup-disponibilidad-content', this.lon, this.lat, this.zoom);
    this.createPlazasLayers();
  }

  createPlazasLayers() {
    const features: Feature[] = [];
    const styleFunction = (feature: Feature, resolution) => this.vectorStyleFunction(feature, resolution);
    this.vectorLayer = new VectorLayer({
      source: new Cluster({
        distance: CLUSTER_PIXEL_DISTANCE,
        source: new VectorSource({
          features
        })
      }),
      style: styleFunction
    });
    this.map.addLayer(this.vectorLayer);
    this.loadFeatures(true);
    this.setClickEventPlazaMarker();
  }

  protected loadFeatures(zoomToExtent: boolean) {
    (this.vectorLayer.getSource() as Cluster).getSource().clear();
    const features: Feature[] = [];
    this.plazas.forEach((regulacionPlazaInforme) => {
      const item = this.itemLayers.find((lyrItem) => lyrItem.type === regulacionPlazaInforme.regulacionPlaza.plaza.tipoPlaza.id);
      if (item && item.active) {
        features.push(new Feature({
          tipoPlaza_id: regulacionPlazaInforme.regulacionPlaza.plaza.tipoPlaza.id,
          ocupacionMediaSemanal: regulacionPlazaInforme.usoPlaza.ocupacionMediaSemanal,
          geometry: new Point(Proj.fromLonLat([regulacionPlazaInforme.regulacionPlaza.plaza.localizacion.coordinates[1], regulacionPlazaInforme.regulacionPlaza.plaza.localizacion.coordinates[0]])),
          calle: regulacionPlazaInforme.regulacionPlaza.plaza.calle,
          numero: regulacionPlazaInforme.regulacionPlaza.plaza.numero,
          ocupacion: regulacionPlazaInforme.regulacionPlaza.plaza.estadoPlaza.nombre,
          tipoAparcamiento: regulacionPlazaInforme.regulacionPlaza.plaza.tipoAparcamiento ? regulacionPlazaInforme.regulacionPlaza.plaza.tipoAparcamiento.nombre : null,
          tipoPlaza: regulacionPlazaInforme.regulacionPlaza.plaza.tipoPlaza.nombre,
          coordinate: regulacionPlazaInforme.regulacionPlaza.plaza.localizacion.coordinates,
          tiempoMaxPermitido: regulacionPlazaInforme.regulacionPlaza.tiempoMaxPermitido,
          franjas: regulacionPlazaInforme.regulacionPlaza.franjasHorarias,
          pma: regulacionPlazaInforme.regulacionPlaza.pma,
          ocupacionMediaUltimaSemana: regulacionPlazaInforme.usoPlaza.ocupacionMediaSemanal
        }));
      }
    });
    (this.vectorLayer.getSource() as Cluster).getSource().addFeatures(features);
    if (zoomToExtent) {
      const extent: olExtent.Extent = (this.vectorLayer.getSource() as Cluster).getSource().getExtent();
      if (extent[0] !== Number.POSITIVE_INFINITY) {
        if (!this.isEmptyExtent(extent)) {
          this.fitExtent(extent, {
            duration: DEFAULT_ANIMATION_MILLISECONDS
          }, true);
        }
      }
    }
  }

  protected vectorStyleFunction(feature: Feature, resolution) {
    let style;
    const size = feature.get('features').length;
    if (size === 1) {
      style = new Style({
        image: new Icon({
          anchor: [0.5, 0.5],
          src: this.getIconPlaza(feature.get('features')[0]),
          scale: 0.5,
        })
      });
    } else {
      style = new Style({
        image: new Icon({
          anchorXUnits: IconAnchorUnits.FRACTION,
          anchorYUnits: IconAnchorUnits.FRACTION,
          anchor: [0.5, 0.5],
          src: environment.icon_map.cluster,
          scale: 1,
        }),
        text: new textStyle.default({
          text: size.toString(),
          offsetY: -2,
          stroke: new Stroke({
            color: '#fff',
            width: 1
          }),
          textBaseline: 'middle',
          font: '12px Calibri,sans-serif',
          textAlign: 'center',
          fill: new Fill({
            color: '#fff'
          })
        })
      });
    }
    return style;
  }

  protected setSize() {
    if (this.mapEl) {
      const styles = this.mapEl.style;
      styles.height = coerceCssPixelValue(this.height) || environment.default_height_map;
      styles.width = coerceCssPixelValue(this.width) || environment.default_width_map;
    }
  }

  getIconPlaza(feature: Feature) {
    switch (feature.get('tipoPlaza_id')) {
      case VE_TYPE:
        if (feature.get('ocupacionMediaSemanal') === 0) {
          return environment.icon_map.VE.green;
        } else if (feature.get('ocupacionMediaSemanal') < 30) {
          return environment.icon_map.VE.blue;
        } else if (feature.get('ocupacionMediaSemanal') > 30 && feature.get('ocupacionMediaSemanal') < 120) {
          return environment.icon_map.VE.yellow;
        } else if (feature.get('ocupacionMediaSemanal') > 120) {
          return environment.icon_map.VE.red;
        } else {
          return environment.icon_map.VE.black;
        }
      case PMR_TYPE:
        /*if (feature.get('ocupacionMediaSemanal') === 0) {
          return environment.icon_map.PMR.green;
        } else if ( feature.get('ocupacionMediaSemanal') < 30) {
          return environment.icon_map.PMR.blue;
        } else if (  feature.get('ocupacionMediaSemanal') > 30 && feature.get('ocupacionMediaSemanal') < 120) {
          return environment.icon_map.PMR.yellow;
        } else if ( feature.get('ocupacionMediaSemanal') > 120) {
          return environment.icon_map.PMR.red;
        } else {*/
        return environment.icon_map.PMR.default;
      // }
      case CYD_TYPE:
        if (feature.get('ocupacionMediaSemanal') === 0) {
          return environment.icon_map.CyD.green;
        } else if (feature.get('ocupacionMediaSemanal') < 30) {
          return environment.icon_map.CyD.blue;
        } else if (feature.get('ocupacionMediaSemanal') > 30 && feature.get('ocupacionMediaSemanal') < 120) {
          return environment.icon_map.CyD.yellow;
        } else if (feature.get('ocupacionMediaSemanal') > 120) {
          return environment.icon_map.CyD.red;
        } else {
          return environment.icon_map.CyD.black;
        }
      default:
        return environment.icon_map.CyD.black;
    }
  }

  public setClickEventPlazaMarker() {
    this.map.on('pointermove', (evt) => {
      const hit = this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
        return (feature && (feature.get('features')));
      });
      if (hit) {
        this.map.getTargetElement().style.cursor = 'pointer';
      } else {
        this.map.getTargetElement().style.cursor = '';
      }
    });
    this.map.on('singleclick', (evt) => {
      this.displayInfoPlaza(evt);
    });
  }

  private expandClusterFeature(clusterFeature: Feature, evt: MapBrowserEvent) {
    const originalFeatures = clusterFeature.get('features');
    let extent: olExtent.Extent = olExtent.createEmpty();
    originalFeatures.forEach((f: Feature) => {
      extent = olExtent.extend(extent, f.getGeometry().getExtent());
    });
    if (extent[0] !== Number.POSITIVE_INFINITY) {
      if (!this.isEmptyExtent(extent)) {
        this.fitExtent(extent, {
          duration: DEFAULT_ANIMATION_MILLISECONDS
        }, true);
      } else {
        this.showPopup(originalFeatures, evt);
      }
    }
  }

  activateItem(item) {
    item.active = !item.active;
    this.loadFeatures(false);
  }

  private showPopup(features: Feature[], evt: MapBrowserEvent) {
    const info = [];
    let existFeature = false;
    let coordinates = null;
    let first = true;
    features.forEach((feature: Feature) => {
      if (!first) {
        info.push('<span></span>');
      }
      let infoFranja = '';
      if (feature.get('tipoAparcamiento') !== null) {
        info.push(`<b>Tipo aparcamiento: </b> ${feature.get('tipoAparcamiento')}`);
      }
      if (feature.get('calle') !== null && feature.get('numero') !== null) {
        info.push(`<b>Dirección: </b> ${feature.get('calle')}, ${feature.get('numero')}`);
      } else if (feature.get('calle') !== null) {
        info.push(`<b>Dirección :</b> ${feature.get('calle')}`);
      }
      if (feature.get('franjas') != null) {
        feature.get('franjas').forEach((franja) => {
          infoFranja += `${franja.horaInicio}/${franja.horaFin}<br/>`;
        });
      }
      info.push(`<b>Tipo de plaza : </b>${feature.get('tipoPlaza')}`);
      if (feature.get('tipoPlaza') !== 'PMR') {
        info.push(`<b>Estado :</b> ${feature.get('ocupacion')}`);
        info.push(`<b>Tiempo max permitido :</b>${feature.get('tiempoMaxPermitido')}min`);
        info.push(`<b>PMA :</b>${feature.get('pma')}`);
        info.push(`<b>Franjas Horarias :</b>${infoFranja}`);
      }
      existFeature = true;
      if (!coordinates) {
        coordinates = this.getHitCoordinates(this.map.getCoordinateFromPixel(evt.pixel), feature.getGeometry());
      }
      first = false;
    });

    if (existFeature) {
      this.contentPopup.innerHTML = info.join('<br/>');
      setTimeout(() => {
        this.overlayPopUp.setOffset([0, -20]);
        this.overlayPopUp.setPosition(coordinates ? coordinates : evt.coordinate);
      }, 0);

    } else {
      this.overlayPopUp.setPosition(undefined);
    }
  }

  private displayInfoPlaza = (evt: MapBrowserEvent) => {
    this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
      if (this.isClusterFeature(feature)) {
        this.expandClusterFeature(feature, evt);
      } else {
        this.showPopup(feature.get('features'), evt);
      }
    });
  }

  closePopUp() {
    this.overlayPopUp.setPosition(undefined);
    return false;
  }
}

const cssUnitsPattern = /([A-Za-z%]+)$/;

function coerceCssPixelValue(value: any): string {
  if (value == null) {
    return '';
  }

  return cssUnitsPattern.test(value) ? value : `${value}px`;
}
